import { Image, Layers, Monitor, ZoomIn, ZoomOut, Maximize, Grid3x3 as Grid3X3, Tag, ShieldCheck, RotateCcw, ChevronLeft } from 'lucide-react';
import { usePreviewStore } from '../../../stores/previewStore';
import { PREVIEW_MODES } from '../../../types/preview';
import type { PreviewMode } from '../../../types/preview';

const MODE_ICONS: Record<PreviewMode, React.ReactNode> = {
  background: <Image className="w-3.5 h-3.5" />,
  overlay: <Layers className="w-3.5 h-3.5" />,
  combined: <Monitor className="w-3.5 h-3.5" />,
};

export default function PreviewToolbar() {
  const {
    previewMode,
    setPreviewMode,
    previewZoom,
    setPreviewZoom,
    showBoundaries,
    toggleBoundaries,
    showLayerIndicators,
    toggleLayerIndicators,
    showADAPanel,
    toggleADAPanel,
    runADAChecks,
    adaCheckResults,
    exitPreview,
    resolveAllElements,
  } = usePreviewStore();

  const errorCount = adaCheckResults.filter((r) => r.severity === 'error').length;
  const warningCount = adaCheckResults.filter((r) => r.severity === 'warning').length;

  const handleZoomIn = () => setPreviewZoom(Math.min(previewZoom + 0.1, 3));
  const handleZoomOut = () => setPreviewZoom(Math.max(previewZoom - 0.1, 0.25));
  const handleResetZoom = () => setPreviewZoom(1);

  const handleRefresh = () => {
    resolveAllElements();
    runADAChecks();
  };

  return (
    <div className="flex items-center h-10 px-2 bg-[#12121a] border-b border-white/[0.06] gap-1 shrink-0">
      <button
        onClick={exitPreview}
        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs text-white/70 hover:text-white hover:bg-white/[0.06] transition-colors"
        title="Exit preview (Escape)"
      >
        <ChevronLeft className="w-3.5 h-3.5" />
        <span>Editor</span>
      </button>

      <div className="w-px h-5 bg-white/[0.08] mx-1" />

      <div className="flex items-center bg-white/[0.04] rounded-lg p-0.5">
        {PREVIEW_MODES.map((mode) => {
          const isActive = previewMode === mode.key;
          return (
            <button
              key={mode.key}
              onClick={() => setPreviewMode(mode.key)}
              className={`flex items-center gap-1.5 px-3 py-1 rounded-md text-xs transition-all ${
                isActive
                  ? 'bg-purple-500/20 text-purple-300 shadow-sm'
                  : 'text-white/50 hover:text-white/70 hover:bg-white/[0.04]'
              }`}
              title={`${mode.description} (${mode.shortcut})`}
            >
              {MODE_ICONS[mode.key]}
              <span className="font-medium">{mode.label}</span>
            </button>
          );
        })}
      </div>

      <div className="flex-1" />

      <button
        onClick={handleRefresh}
        className="p-1.5 rounded-md text-white/40 hover:text-white/70 hover:bg-white/[0.06] transition-colors"
        title="Refresh preview"
      >
        <RotateCcw className="w-3.5 h-3.5" />
      </button>

      <button
        onClick={toggleBoundaries}
        className={`p-1.5 rounded-md transition-colors ${
          showBoundaries
            ? 'bg-purple-500/20 text-purple-300'
            : 'text-white/40 hover:text-white/70 hover:bg-white/[0.06]'
        }`}
        title="Show element boundaries (B)"
      >
        <Grid3X3 className="w-3.5 h-3.5" />
      </button>

      <button
        onClick={toggleLayerIndicators}
        className={`p-1.5 rounded-md transition-colors ${
          showLayerIndicators
            ? 'bg-purple-500/20 text-purple-300'
            : 'text-white/40 hover:text-white/70 hover:bg-white/[0.06]'
        }`}
        title="Show layer indicators (L)"
      >
        <Tag className="w-3.5 h-3.5" />
      </button>

      <div className="w-px h-5 bg-white/[0.08] mx-1" />

      <button
        onClick={() => {
          if (!showADAPanel) runADAChecks();
          toggleADAPanel();
        }}
        className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs transition-colors ${
          showADAPanel
            ? 'bg-purple-500/20 text-purple-300'
            : 'text-white/50 hover:text-white/70 hover:bg-white/[0.06]'
        }`}
        title="ADA Compliance Check (A)"
      >
        <ShieldCheck className="w-3.5 h-3.5" />
        <span className="font-medium">ADA</span>
        {errorCount > 0 && (
          <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-red-500/20 text-red-400">
            {errorCount}
          </span>
        )}
        {warningCount > 0 && errorCount === 0 && (
          <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-amber-500/20 text-amber-400">
            {warningCount}
          </span>
        )}
        {errorCount === 0 && warningCount === 0 && adaCheckResults.length > 0 && (
          <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-emerald-500/20 text-emerald-400">
            OK
          </span>
        )}
      </button>

      <div className="w-px h-5 bg-white/[0.08] mx-1" />

      <button
        onClick={handleZoomOut}
        className="p-1.5 rounded-md text-white/40 hover:text-white/70 hover:bg-white/[0.06] transition-colors"
        title="Zoom out (-)"
      >
        <ZoomOut className="w-3.5 h-3.5" />
      </button>

      <button
        onClick={handleResetZoom}
        className="px-2 py-1 rounded-md text-xs text-white/60 hover:text-white/80 hover:bg-white/[0.06] transition-colors min-w-[3rem] text-center font-mono"
        title="Reset zoom to 100% (0)"
      >
        {Math.round(previewZoom * 100)}%
      </button>

      <button
        onClick={handleZoomIn}
        className="p-1.5 rounded-md text-white/40 hover:text-white/70 hover:bg-white/[0.06] transition-colors"
        title="Zoom in (+)"
      >
        <ZoomIn className="w-3.5 h-3.5" />
      </button>

      <button
        onClick={handleResetZoom}
        className="p-1.5 rounded-md text-white/40 hover:text-white/70 hover:bg-white/[0.06] transition-colors"
        title="Fit to view"
      >
        <Maximize className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
